// Puzzle Randomization Scan
// Verifies puzzle order is truly randomized and not alphabetical

import { ScanResult, createScanSummary, ScanSummary } from './types';
import { getShuffledSolutions, validateNoRepeats, verifyPRNGConsistency, getPuzzleSequence } from '@/utils/wordlistEngine';
import { checkShuffleUniformity, detectAlphabeticalSegments, analyzePuzzleSequence } from '@/utils/puzzleOrderValidator';

export function runPuzzleRandomizationScan(): ScanSummary {
  console.group('🔀 PUZZLE RANDOMIZATION SCAN');
  console.log('Verifying puzzle order is properly randomized...\n');
  
  const results: ScanResult[] = [];
  const startTime = Date.now();
  
  // Get shuffled word list
  const shuffledWords = getShuffledSolutions();
  
  // Check 1: PRNG consistency
  if (verifyPRNGConsistency()) {
    results.push({
      name: 'PRNG Consistency',
      status: 'pass',
      message: 'Seeded PRNG produces consistent results'
    });
  } else {
    results.push({
      name: 'PRNG Consistency',
      status: 'fail',
      message: 'PRNG is not producing consistent results - shuffle may vary!'
    });
  }
  
  // Check 2: No repeats in shuffled list
  if (validateNoRepeats()) {
    results.push({
      name: 'No Repeats',
      status: 'pass',
      message: 'Shuffled list contains no duplicate words'
    });
  } else {
    results.push({
      name: 'No Repeats',
      status: 'fail',
      message: 'Shuffled list contains duplicate words!'
    });
  }
  
  // Check 3: Alphabetical segments in shuffled order
  const alphabeticalSegments = detectAlphabeticalSegments(shuffledWords);
  if (alphabeticalSegments.length === 0) {
    results.push({
      name: 'Alphabetical Segments',
      status: 'pass',
      message: 'No alphabetical patterns found in shuffled order'
    });
  } else if (alphabeticalSegments.length <= 2) {
    results.push({
      name: 'Alphabetical Segments',
      status: 'warn',
      message: `Found ${alphabeticalSegments.length} small alphabetical segments (acceptable)`,
      details: `Longest segment: ${alphabeticalSegments[0]?.words.length || 0} words`
    });
  } else {
    results.push({
      name: 'Alphabetical Segments',
      status: 'fail',
      message: `Found ${alphabeticalSegments.length} alphabetical patterns in shuffled order`,
      details: 'Shuffle may not be working correctly'
    });
  }
  
  // Check 4: Shuffle uniformity
  const uniformity = checkShuffleUniformity(shuffledWords);
  if (uniformity.uniformityScore >= 60) {
    results.push({
      name: 'Shuffle Uniformity',
      status: 'pass',
      message: `Excellent shuffle uniformity (score: ${uniformity.uniformityScore.toFixed(1)}/100)`
    });
  } else if (uniformity.uniformityScore >= 50) {
    results.push({
      name: 'Shuffle Uniformity',
      status: 'warn',
      message: `Moderate shuffle uniformity (score: ${uniformity.uniformityScore.toFixed(1)}/100)`,
      details: uniformity.warnings.join('; ')
    });
  } else {
    results.push({
      name: 'Shuffle Uniformity',
      status: 'fail',
      message: `Poor shuffle uniformity (score: ${uniformity.uniformityScore.toFixed(1)}/100)`,
      details: uniformity.warnings.join('; ')
    });
  }
  
  // Check 5: Max consecutive cluster size
  if (uniformity.maxClusterSize <= 4) {
    results.push({
      name: 'Letter Clustering',
      status: 'pass',
      message: `No significant letter clustering (max: ${uniformity.maxClusterSize} consecutive)`
    });
  } else if (uniformity.maxClusterSize <= 6) {
    results.push({
      name: 'Letter Clustering',
      status: 'warn',
      message: `Moderate letter clustering detected (max: ${uniformity.maxClusterSize} consecutive)`
    });
  } else {
    results.push({
      name: 'Letter Clustering',
      status: 'fail',
      message: `High letter clustering detected (max: ${uniformity.maxClusterSize} consecutive)`
    });
  }
  
  // Check 6: Analyze recent puzzle sequence
  const today = new Date();
  const recentPuzzles = analyzePuzzleSequence(today, 30);
  
  if (!recentPuzzles.hasDuplicates) {
    results.push({
      name: 'Recent Puzzle Duplicates',
      status: 'pass',
      message: 'No duplicates in last 30 puzzles'
    });
  } else {
    results.push({
      name: 'Recent Puzzle Duplicates',
      status: 'fail',
      message: 'Found duplicate words in recent puzzle sequence!'
    });
  }
  
  if (!recentPuzzles.hasAlphabeticalPattern) {
    results.push({
      name: 'Recent Puzzle Order',
      status: 'pass',
      message: 'Recent puzzles are properly randomized (not alphabetical)'
    });
  } else {
    results.push({
      name: 'Recent Puzzle Order',
      status: 'fail',
      message: 'Recent puzzles show alphabetical patterns!',
      details: 'Puzzles may be appearing in alphabetical order'
    });
  }
  
  // Check 7: Sample future puzzles (next 10 days)
  const futurePuzzles = getPuzzleSequence(new Date(), 10);
  const futureWords = futurePuzzles.map(p => p.word);
  const futureAlphabetical = detectAlphabeticalSegments(futureWords);
  
  if (futureAlphabetical.length === 0) {
    results.push({
      name: 'Future Puzzle Order',
      status: 'pass',
      message: 'Next 10 puzzles are properly randomized'
    });
  } else {
    results.push({
      name: 'Future Puzzle Order',
      status: 'warn',
      message: `Next 10 puzzles contain ${futureAlphabetical.length} alphabetical segment(s)`
    });
  }
  
  // Check 8: Verify shuffle is different from original
  const originalWords = [...shuffledWords].sort();
  const isDifferentFromAlphabetical = shuffledWords.some((word, idx) => word !== originalWords[idx]);
  
  if (isDifferentFromAlphabetical) {
    results.push({
      name: 'Shuffle vs Original',
      status: 'pass',
      message: 'Shuffled order is different from alphabetical original'
    });
  } else {
    results.push({
      name: 'Shuffle vs Original',
      status: 'fail',
      message: 'Shuffled order matches alphabetical order - shuffle not working!'
    });
  }
  
  const duration = ((Date.now() - startTime) / 1000).toFixed(2);
  
  console.log('\n' + '='.repeat(50));
  console.log('📊 PUZZLE RANDOMIZATION SUMMARY');
  console.log('='.repeat(50));
  console.log(`Uniformity Score: ${uniformity.uniformityScore.toFixed(1)}/100`);
  console.log(`Max Cluster Size: ${uniformity.maxClusterSize}`);
  console.log(`Alphabetical Segments: ${alphabeticalSegments.length}`);
  console.log(`Recent Duplicates: ${recentPuzzles.hasDuplicates ? 'YES ⚠️' : 'NO ✓'}`);
  console.log(`Recent Alphabetical: ${recentPuzzles.hasAlphabeticalPattern ? 'YES ⚠️' : 'NO ✓'}`);
  console.log(`Duration: ${duration}s`);
  console.log('='.repeat(50));
  console.groupEnd();
  
  return createScanSummary('Puzzle Randomization Scan', results);
}
